import axios from 'src/api/axiosInstances/privateAxiosInstancePrice';
import { useEffect, useState } from 'react';
import { notification } from 'antd';
import auth from 'src/services/auth';

type AsyncDataResponse = {
  successCode: number;
  successMessage: string;
}

type ProductCleanupListRequest = {
  verticalName?: string;
  siteId?: string;
  fingerprintId?: string;
  manufacturersAreEqual?: string;
  size?: number;
  page?: number;
  sort?: string;
  direction?: string;
};

const transformParams = (params: ProductCleanupListRequest | null) => {
  return { ...params };
};

const transformData = (data: any) => {
  const array: any = [];

  data.forEach((item: any) => {
    array.push(
      {
        isDuplicate: true,
        duplicatedProduct: item.duplicatedProduct,
        originalProduct: item.originalProduct,
        ...item.duplicatedProduct,
      },
      { duplicatedProduct: item.duplicatedProduct, originalProduct: item.originalProduct, ...item.originalProduct }
    );
  });

  return array;
};

const showNotification = (message: string) => {
  notification.error({
    message: 'An error occurred.',
    description: message,
    duration: 0,
  });
};

const getAsyncData = async (url: string, userId: string, delay: number): Promise<any> => {
  try {
    const response: any = await axios.get(url, {
      headers: { userId },
    });
    if (response.status !== 200) {
      if (response.status === 206) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        return getAsyncData(url, userId, delay);
      } else {
        throw new Error(`Response status is ${response.status}. Stopping retries.`);
      }
    }   
    return response.data;
  } catch (error) {
    console.error(`Error fetching async data :`, error);
    throw error;
  }
};


export const useGetProductCleanupList = (params: ProductCleanupListRequest | null): [boolean, number, any[]] => {
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [productList, setProductList] = useState<any[]>([]);
  const userId = auth.getUserId();

  useEffect(() => {
    const fetchData = async () => {
      if (params && params.verticalName && params.siteId && params.fingerprintId) {
        setLoading(true);
        try {
          const response = await axios.get('/oc/product/retrieveduplicates', {
            headers: { userId },
            params: transformParams(params),
          });

          if (response && response.headers && response.headers.location) {
            const location = response.headers.location;
            const delay = 1000;
            setTimeout(async () => {
              try {
                const asyncData = await getAsyncData(`/nucleus/asynch?fileName=${location}`, userId, delay);
                if (asyncData && asyncData.duplicateProducts) {
                  setProductList(transformData(asyncData.duplicateProducts));
                  setTotalRecords(asyncData.totalCount);
                } else {
                  setProductList([]);
                  setTotalRecords(0);
                  showNotification('No duplicate products found.');
                }
              } catch (error) {
                console.error('Error fetching async data:', error);
                setProductList([]);
                setTotalRecords(0);
              } finally {
                setLoading(false);
              }
            }, delay);
          } else {
            console.error('Location header missing in response:', response);
            setProductList([]);
            setTotalRecords(0);
            setLoading(false);
          }
        } catch (error) {
          console.error('Error fetching initial data:', error);
          setProductList([]);
          setTotalRecords(0);
          setLoading(false);
        }
      } else {
        setProductList([]);
        setTotalRecords(0);
      }
    };
    fetchData();
  }, [params, userId]);

  return [loading, totalRecords, productList];
};

export const useMergeProducts = (
  params: ProductCleanupListRequest | null,
  productList: any[],
  setMergeProductList: (list: any[]) => void,
  onUpdate?: (length: number) => void,
  setRequestLoading?: (loading: boolean) => void
) => {
  useEffect(() => {
    const retryFetchAsyncData = async (location: string, userId: string) => {
      const delay = 1000;
      try {
        await new Promise((resolve) => setTimeout(resolve, delay));
        const response: any = await axios.get<AsyncDataResponse>(`/nucleus/asynch?fileName=${location}`, {
          headers: { userId },
        });

        if (response.status === 200) {
          const asyncData = response.data;
          if (asyncData.successCode === 200) {
            setRequestLoading && setRequestLoading(false);
            setMergeProductList([]);
            onUpdate && onUpdate(productList.length);
            notification.success({
              message: asyncData.successMessage,
              placement: 'topRight',
            });
            return;
          } else {
            throw new Error('Async data is not successful.');
          }
        } else if (response.status === 206) {
          setTimeout(() => retryFetchAsyncData(location, userId), delay);
        } else {
          setRequestLoading && setRequestLoading(false);
          throw new Error(`Response status is ${response.status}. Stopping retries.`);
        }
      } catch (error) {
        console.error(`Error in async data fetch:`, error);
        setRequestLoading && setRequestLoading(false);
      }
    };

    if (productList.length > 0 && params && params.verticalName) {
      const userId = auth.getUserId();
      setRequestLoading && setRequestLoading(true);
      axios.post('/oc/product/mergeduplicates', {
        duplicates: productList,
      }, {
        params: {
          verticalName: params.verticalName,
        },
      })
        .then((response: any) => {
          if (response && response.headers && response.headers.location) {
            retryFetchAsyncData(response.headers.location, userId); // No maxRetries, will loop indefinitely
          } else {
            setRequestLoading && setRequestLoading(false);
            console.error('Location header missing in response:', response);
          }
        })
        .catch((error: any) => {
          setRequestLoading && setRequestLoading(false);
          console.error('Error making mergeduplicates request:', error);
        });
    }
  }, [params, productList]);

  return [];
};


export const useMergeAll = (
  isMergeAll: boolean,
  setMergedAll: (value: boolean) => void,
  params: ProductCleanupListRequest | null,
  setRequestParams: (params: any) => void,
  setRequestLoading?: (loading: boolean) => void
) => {
  useEffect(() => {
    const retryFetchAsyncData = async (location: string, userId: string) => {
      const delay = 3000;

      try {
        await new Promise((resolve) => setTimeout(resolve, delay)); // Initial delay before each call
        const response: any = await axios.get<AsyncDataResponse>(`/nucleus/asynch?fileName=${location}`, {
          headers: { userId },
        });

        if (response.status === 200) {
          const asyncData = response.data;
          if (asyncData.successCode === 200) {
            setRequestLoading && setRequestLoading(false);
            setRequestParams({
              ...params,
              pagestart: 0,
            });
            notification.success({
              message: asyncData.successMessage,
              placement: 'topRight',
            });
            return;
          } else {
            throw new Error('Async data is not successful.');
          }
        } else if (response.status === 206) {
          setTimeout(() => retryFetchAsyncData(location, userId), delay);
        } else {
          setRequestLoading && setRequestLoading(false);
          throw new Error(`Response status is ${response.status}. Stopping retries.`);
        }
      } catch (error) {
        console.error(`Error in async data fetch:`, error);
        setRequestLoading && setRequestLoading(false);
      }
    };

    if (isMergeAll && params && params.verticalName) {
      const userId = auth.getUserId();
      setRequestLoading && setRequestLoading(true);
      setMergedAll(false);

      axios.post('/oc/product/mergeduplicates/all', {}, {
        headers: {
          userId: userId,
        },
        params: {
          fingerprintId: params.fingerprintId,
          siteId: params.siteId,
          verticalName: params.verticalName,
          manufacturersAreEqual: params.manufacturersAreEqual,
        },
      })
        .then((response: any) => {
          if (response && response.headers && response.headers.location) {
            retryFetchAsyncData(response.headers.location, userId); // No maxRetries, will loop indefinitely
          } else {
            setRequestLoading && setRequestLoading(false);
            setRequestParams({
              ...params,
              pagestart: 0,
            });
            console.error('Location header missing in response:', response);
          }
        })
        .catch((error: any) => {
          setRequestLoading && setRequestLoading(false);
          console.error('Error making mergeduplicates/all request:', error);
        });
    }
  }, [isMergeAll, params]);

  return [];
};


export const useDifferentiateProducts = (
  params: ProductCleanupListRequest | null,
  productList: any[],
  setDifferentiateProductList: (list: any[]) => void,
  onUpdate?: (length: number) => void,
  setRequestLoading?: (loading: boolean) => void
) => {
  useEffect(() => {
    const retryFetchAsyncData = async (location: string, userId: string) => {
      const delay = 1000;

      try {
        await new Promise((resolve) => setTimeout(resolve, delay));
        const response: any = await axios.get<AsyncDataResponse>(`/nucleus/asynch?fileName=${location}`, {
          headers: { userId },
        });

        if (response.status === 200) {
          const asyncData = response.data;
          if (asyncData.successCode === 200) {
            setRequestLoading && setRequestLoading(false);
            setDifferentiateProductList([]);
            onUpdate && onUpdate(productList.length);
            notification.success({
              message: asyncData.successMessage,
              placement: 'topRight',
            });

            return;
          } else {
            throw new Error('Async data is empty or invalid.');
          }
        } else if (response.status === 206) {
          setTimeout(() => retryFetchAsyncData(location, userId), delay);
        } else {
          setRequestLoading && setRequestLoading(false);
          throw new Error(`Response status is ${response.status}. Stopping retries.`);
        }
      } catch (error) {
        console.error(`Error in async data fetch:`, error);
        setRequestLoading && setRequestLoading(false);
      }
    };

    if (productList.length > 0 && params && params.verticalName) {
      const userId = auth.getUserId();
      setRequestLoading && setRequestLoading(true);
      axios
        .post(
          '/oc/product/ignoreduplicates',
          { duplicates: productList },
          { params: { verticalName: params.verticalName } }
        )
        .then((response: any) => {
          if (response && response.headers && response.headers.location) {
            retryFetchAsyncData(response.headers.location, userId); // No maxRetries, will loop indefinitely
          } else {
            setRequestLoading && setRequestLoading(false);
            console.error('Location header missing in response:', response);
          }
        })
        .catch((error: any) => {
          setRequestLoading && setRequestLoading(false);
          console.error('Error making ignoreduplicates request:', error);
        });
    }
  }, [params, productList]);

  return [];
};

export const useDifferentiateAll = (
  isDifferentiateAll: boolean,
  setDifferentiatedAll: (value: boolean) => void,
  params: ProductCleanupListRequest | null,
  setRequestParams: (params: any) => void,
  setRequestLoading?: (loading: boolean) => void
) => {
  useEffect(() => {
    const retryFetchAsyncData = async (location: string, userId: string) => {
      const delay = 3000;

      try {
        await new Promise((resolve) => setTimeout(resolve, delay)); // Initial delay before each call
        const response: any = await axios.get<AsyncDataResponse>(`/nucleus/asynch?fileName=${location}`, {
          headers: { userId },
        });

        if (response.status === 200) {
          const asyncData = response.data;
          if (asyncData.successCode === 200) {
            setRequestLoading && setRequestLoading(false);
            setRequestParams({
              ...params,
              pagestart: 0,
            });
            notification.success({
              message: asyncData.successMessage,
              placement: 'topRight',
            });
            return;
          } else {
            throw new Error('Async data is not successful.');
          }
        } else if (response.status === 206) {
          setTimeout(() => retryFetchAsyncData(location, userId), delay);
        } else {
          setRequestLoading && setRequestLoading(false);
          throw new Error(`Response status is ${response.status}. Stopping retries.`);
        }
      } catch (error) {
        console.error(`Error in async data fetch:`, error);
        setRequestLoading && setRequestLoading(false);
      }
    };

    if (isDifferentiateAll && params && params.verticalName) {
      const userId = auth.getUserId();
      setRequestLoading && setRequestLoading(true);
      setDifferentiatedAll(false);

      axios.post('/oc/product/ignoreduplicates/all', {}, {
        params: {
          fingerprintId: params.fingerprintId,
          siteId: params.siteId,
          verticalName: params.verticalName,
          manufacturersAreEqual: params.manufacturersAreEqual,
        },
      })
        .then((response: any) => {
          if (response && response.headers && response.headers.location) {
            retryFetchAsyncData(response.headers.location, userId); // No maxRetries, will loop indefinitely
          } else {
            setRequestLoading && setRequestLoading(false);
            console.error('Location header missing in response:', response);
          }
        })
        .catch((error: any) => {
          setRequestLoading && setRequestLoading(false);
          console.error('Error making ignoreduplicates/all request:', error);
        });
    }
  }, [isDifferentiateAll, params]);
  return [];
};
